import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@material-ui/core';
import { useStyles } from './styles';
import { extractApiErrorMessage, notifications } from '../../utils';
import { FarmMedicalSupplyViewModel } from '../../models/injection';
import { MedicalSuppliesService } from '../../hooks/MedicalSuppliesService';
import { FarmMedicalSupplySelect, Loader, AnimalTypeSelect } from '../../elements';
import { LoaderSize } from '../../constants/constants';
import { AnimalType } from '../../models/animal';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import {
  HealthSystemDALEnumsAnimalType,
  HealthSystemDomainVaccinationRulesCreateVaccinationRuleCommand
} from '../../api/HealthSystemApi';

interface CreateVaccinationRuleModalProps {
  opened: boolean;
  onModalAction: (result: boolean) => void;
}

const CreateVaccinationRuleModal: React.FC<any> = ({ opened, onModalAction }: CreateVaccinationRuleModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const medicalSuppliesService = new MedicalSuppliesService();
  const apiService = new ApiService();

  const [animalType, setAnimalType] = React.useState<HealthSystemDALEnumsAnimalType>(0);
  const [ageFrom, setAgeFrom] = React.useState(1);
  const [ageTo, setAgeTo] = React.useState(1);
  const [dose, setDose] = React.useState(1);

  const [isPregnant, setIsPregnant] = React.useState(false);
  const [isLactating, setIsLactating] = React.useState(false);
  const [isGast, setIsGast] = React.useState(false);

  const [loaded, setLoaded] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [isBusyLoading, setIsBusyLoading] = React.useState(false);

  const [medicalSupplies, setMedicalSupplies] = React.useState([] as FarmMedicalSupplyViewModel[]);
  const [medicalSupplyId, setMedicalSupplyId] = React.useState('');

  React.useEffect(() => {
    if (opened) {
      clearFields();

      if (!loaded) {
        const loadData = async () => loadMedicalSupplies();
        loadData();
      }
    }
  }, [opened]);

  const loadMedicalSupplies = async () => {
    setIsBusyLoading(true);

    medicalSuppliesService
      .getAllMedicalSupplies()
      .then((result) => {
        setMedicalSupplies(result);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusyLoading(false);
      });
  };

  const handleFarmSupplyChange = (medicalSupplyId: string) => {
    setMedicalSupplyId(medicalSupplyId);
  };

  const closeDialog = (text: string | null) => {
    if (text == null) {
      clearFields();
      onModalAction(false);
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    apiService.api.vaccinationRules
      .vaccinationRulesCreateVaccinationRule(buildCreateVaccinationRuleCommand())
      .then(() => {
        notifications.success(t('f-vaccination-rule-add-ok'));
        onModalAction(true);
        clearFields();
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const buildCreateVaccinationRuleCommand = (): HealthSystemDomainVaccinationRulesCreateVaccinationRuleCommand => {
    return {
      medicalSupplyId,
      dose,
      ageFrom,
      ageTo,
      animalType,
      isPregnant,
      isLactating,
      isGast
    };
  };

  const validateForm = (): boolean => {
    if (animalType === AnimalType.Unknown) {
      notifications.warning(t('f-val-anim-type'));
      return false;
    }

    if (animalType > AnimalType.Gilt) {
      setIsPregnant(false);
      setIsLactating(false);
      setIsGast(false);
    }

    if (!medicalSupplyId) {
      notifications.warning(t('f-val-farm-supply'));
      return false;
    }

    if (ageFrom < 0) {
      notifications.warning(t('f-val-vaccination-rule-age-from'));
      return false;
    }

    if (ageTo < 0) {
      notifications.warning(t('f-val-vaccination-rule-age-to'));
      return false;
    }

    return true;
  };

  const clearFields = () => {
    setIsPregnant(false);
    setIsLactating(false);
    setIsGast(false);
    setAgeFrom(1);
    setAgeTo(1);
    setAnimalType(0);
    setDose(1);
    setMedicalSupplyId('');
  };

  return (
    <Dialog
      id="addVaccinationRuleDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      className={classes.dialog}
      maxWidth="md"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="addVaccinationRuleDialogTitle" className={classes.dialogHeaderFooter}>
        {t('f-vaccination-rule-add-title')}
      </DialogTitle>

      <DialogContent>
        <Grid container direction="column" spacing={2} className={classes.updateForm}>
          <Loader isLoading={isBusyLoading} size={LoaderSize.DEFAULT_SIZE} />

          {loaded && (
            <Grid item container direction="column" spacing={1}>
              <Grid item>
                <AnimalTypeSelect
                  value={animalType}
                  cssClass={classes.formControl}
                  allowAny={true}
                  onAnimalTypeChange={(type) => {
                    setAnimalType(type);
                  }}
                />
              </Grid>

              <Grid item className={classes.formControl}>
                <TextField
                  id="age-from-input"
                  label={t('f-vaccination-rule-age-from')}
                  variant="outlined"
                  fullWidth
                  value={ageFrom}
                  size="small"
                  type="number"
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    if (newValue >= 0) {
                      setAgeFrom(newValue);
                    }
                  }}
                />
              </Grid>

              <Grid item className={classes.formControl}>
                <TextField
                  id="age-to-input"
                  label={t('f-vaccination-rule-age-to')}
                  variant="outlined"
                  fullWidth
                  value={ageTo}
                  size="small"
                  type="number"
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    if (newValue >= 0) {
                      setAgeTo(newValue);
                    }
                  }}
                />
              </Grid>

              <Grid item>
                <FarmMedicalSupplySelect
                  supplies={medicalSupplies}
                  value={medicalSupplyId}
                  onSupplyChange={handleFarmSupplyChange}
                  sx={{ m: 1, minWidth: 320 }}
                />
              </Grid>

              <Grid item className={classes.formControl}>
                <TextField
                  id="dose-input"
                  label={t('f-com-dose')}
                  variant="outlined"
                  fullWidth
                  value={dose}
                  size="small"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{'CC'}</InputAdornment>
                  }}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    if (newValue >= 0) {
                      setDose(newValue);
                    }
                  }}
                />
              </Grid>

              {(animalType === AnimalType.Sow || animalType === AnimalType.Gilt) && (
                <>
                  <FormControlLabel
                    className={classes.marginLeft}
                    control={
                      <Checkbox
                        checked={isPregnant}
                        onChange={(e) => {
                          setIsPregnant(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={t('f-animal-preg-only')}
                  />
                  <FormControlLabel
                    className={classes.marginLeft}
                    control={
                      <Checkbox
                        checked={isLactating}
                        onChange={(e) => {
                          setIsLactating(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={t('f-animal-lact-only')}
                  />
                  <FormControlLabel
                    className={classes.marginLeft}
                    control={
                      <Checkbox
                        checked={isGast}
                        onChange={(e) => {
                          setIsGast(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label={t('f-animal-gast-only')}
                  />
                </>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        {isBusy && <CircularProgress />}

        <Button onClick={() => closeDialog(null)} variant="outlined" startIcon={<Icon>{'close'}</Icon>} color="primary">
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={() => closeDialog('ok')}
          id="addRuleBtn"
          disabled={isBusy}
          startIcon={<Icon>{'add_circle_outline'}</Icon>}
          variant="contained"
          color="primary"
        >
          {t('f-com-create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateVaccinationRuleModal;
